import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, RefreshCw, Upload, X, Check } from 'lucide-react';

const CHAVE_CAMERA = 'analiseCredito.cameraId';

function lerCameraSalva() {
  try { return localStorage.getItem(CHAVE_CAMERA) || ''; } catch { return ''; }
}

function salvarCamera(id) {
  try { localStorage.setItem(CHAVE_CAMERA, id); } catch { /* sem storage — só não lembra */ }
}

// Modal de captura: vídeo ao vivo da câmera → foto JPEG (Blob) entregue em onConfirmar.
// Também aceita escolher um arquivo, para quando a câmera não estiver disponível.
export default function CapturaFoto({ titulo, onConfirmar, onFechar }) {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const inputRef  = useRef(null);

  const [cameras,  setCameras]  = useState([]);
  const [cameraId, setCameraId] = useState(lerCameraSalva);
  const [erro,     setErro]     = useState('');
  const [foto,     setFoto]     = useState(null); // { blob, url }
  const [enviando, setEnviando] = useState(false);

  const pararCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const iniciarCamera = useCallback(async (id) => {
    pararCamera();
    setErro('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setErro('Este navegador não permite acesso à câmera. Use "Enviar arquivo".');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: id
          ? { deviceId: { exact: id }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;

      // Os nomes das câmeras só aparecem depois que a permissão foi concedida
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter(d => d.kind === 'videoinput'));
    } catch (e) {
      if (id && e.name === 'OverconstrainedError') {
        // câmera lembrada não existe mais — tenta a padrão
        setCameraId('');
        return;
      }
      setErro(e.name === 'NotAllowedError'
        ? 'Acesso à câmera negado. Libere a câmera no cadeado da barra de endereço e tente de novo.'
        : 'Não foi possível abrir a câmera. Verifique se ela está conectada ou use "Enviar arquivo".');
    }
  }, [pararCamera]);

  useEffect(() => {
    if (!foto) iniciarCamera(cameraId);
    return pararCamera;
  }, [cameraId, foto, iniciarCamera, pararCamera]);

  useEffect(() => () => { if (foto) URL.revokeObjectURL(foto.url); }, [foto]);

  function capturar() {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      pararCamera();
      setFoto({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.85);
  }

  function escolherArquivo(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setErro('Envie uma imagem JPG ou PNG.');
      return;
    }
    pararCamera();
    setFoto({ blob: file, url: URL.createObjectURL(file) });
  }

  async function confirmar() {
    setEnviando(true);
    try {
      await onConfirmar(foto.blob);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-base font-medium text-gray-900">{titulo}</h2>
          <button onClick={onFechar} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {!foto && cameras.length > 1 && (
            <select
              value={cameraId}
              onChange={e => { setCameraId(e.target.value); salvarCamera(e.target.value); }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
            >
              <option value="">Câmera padrão</option>
              {cameras.map((c, i) => (
                <option key={c.deviceId} value={c.deviceId}>{c.label || `Câmera ${i + 1}`}</option>
              ))}
            </select>
          )}

          <div className="bg-gray-900 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
            {foto ? (
              <img src={foto.url} alt="Prévia" className="w-full h-full object-contain" />
            ) : erro ? (
              <p className="text-sm text-gray-200 text-center px-6">{erro}</p>
            ) : (
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
            )}
          </div>

          <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={escolherArquivo} />

          <div className="flex gap-2 justify-end">
            {foto ? (
              <>
                <button onClick={() => setFoto(null)} disabled={enviando}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                  <RefreshCw size={15} />Tirar de novo
                </button>
                <button onClick={confirmar} disabled={enviando}
                  className="flex items-center gap-2 px-4 py-2 border border-green-400 bg-green-50 text-green-800 rounded-lg text-sm font-medium hover:bg-green-100 disabled:opacity-50">
                  <Check size={15} />{enviando ? 'Salvando...' : 'Usar foto'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  <Upload size={15} />Enviar arquivo
                </button>
                <button onClick={capturar} disabled={!!erro}
                  className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50">
                  <Camera size={15} />Capturar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
